import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Layers,
  TrendingUp,
  Wallet,
  Coins,
  PiggyBank,
  ServerOff,
  AlertCircle,
} from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { CHART, axisProps, gridProps, ChartTooltip, ChartLegend, lineCursor, fmtCompact } from "@/components/ui/chart-kit";
import { getProyeccionEscenarios, getProyeccionConfig, saveProyeccionConfig } from "@/lib/api";

// Colores del tema activo (ver components/ui/chart-kit)
const GUNMETAL = CHART.historico;
const EMERALD = CHART.profit;
const ROSE = CHART.loss;

// Formato único de proyección: una sola fuente en lib/utils (formatEur).
const formatCurrency = formatEur;

const nfPct = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatPctSigned = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${nfPct.format(value)}%`;
};

// Lee un valor de un mapa {anio: valor} tolerando claves string o number
const getAnio = (map, anio) => {
  if (!map) return null;
  const v = map[anio] ?? map[String(anio)];
  return v === undefined ? null : v;
};

// Métricas comparadas entre escenarios (clave = campo del contrato)
const METRICAS = [
  { id: "ventas", label: "Ventas", clave: "ventas_por_anio", icon: TrendingUp },
  { id: "ebitda", label: "EBITDA", clave: "ebitda_por_anio", icon: Wallet },
  { id: "resultado", label: "Resultado neto", clave: "resultado_neto_por_anio", icon: Coins },
  { id: "tesoreria", label: "Tesorería", clave: "tesoreria_final_por_anio", icon: PiggyBank },
];

const LABEL_LARGO = {
  ventas: "Ventas",
  ebitda: "EBITDA",
  resultado: "Resultado neto",
  tesoreria: "Tesorería final",
};

const ESCENARIOS = [
  {
    id: "base",
    label: "Base",
    badge: "border-primary/40 text-primary",
    texto: "text-foreground",
  },
  {
    id: "optimista",
    label: "Optimista",
    badge: "border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400",
    texto: "text-emerald-600 dark:text-emerald-400",
  },
  {
    id: "pesimista",
    label: "Pesimista",
    badge: "border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-400",
    texto: "text-rose-600 dark:text-rose-400",
  },
];

// ---------------------------------------------------------------------------
// Inputs compactos de deltas (pp sobre el crecimiento de ingresos)
// PUT parcial de config onBlur + recarga. El pesimista se muestra en positivo
// con prefijo "−" y se guarda en negativo (contrato: pesimista <= 0).
// ---------------------------------------------------------------------------

function DeltasEscenarios({ config, readOnly, onGuardar }) {
  const escCfg = config?.escenarios || {};
  const optActual = escCfg.optimista_delta_pp ?? 2;
  const pesActual = escCfg.pesimista_delta_pp ?? -2;

  const [optTexto, setOptTexto] = useState(String(optActual));
  const [pesTexto, setPesTexto] = useState(String(Math.abs(pesActual)));

  useEffect(() => {
    setOptTexto(String(optActual));
    setPesTexto(String(Math.abs(pesActual)));
  }, [optActual, pesActual]);

  const parsear = (texto) => {
    const t = String(texto ?? "").trim().replace(",", ".");
    if (t === "") return null;
    const v = parseFloat(t);
    return isNaN(v) ? null : v;
  };

  const handleOptBlur = () => {
    const v = parsear(optTexto);
    if (v === null || v < 0) {
      setOptTexto(String(optActual));
      return;
    }
    if (v === optActual) return;
    onGuardar(
      { escenarios: { optimista_delta_pp: v, pesimista_delta_pp: pesActual } },
      "Escenario optimista actualizado"
    );
  };

  const handlePesBlur = () => {
    const v = parsear(pesTexto);
    if (v === null || v < 0) {
      setPesTexto(String(Math.abs(pesActual)));
      return;
    }
    const nuevo = -Math.abs(v);
    if (nuevo === pesActual) return;
    onGuardar(
      { escenarios: { optimista_delta_pp: optActual, pesimista_delta_pp: nuevo } },
      "Escenario pesimista actualizado"
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Label
            htmlFor="delta-optimista"
            className="text-xs whitespace-nowrap text-emerald-700 dark:text-emerald-400 font-medium"
          >
            Optimista +
          </Label>
          <Input
            id="delta-optimista"
            type="number"
            min="0"
            step="0.5"
            className="h-8 w-[72px] text-sm"
            value={optTexto}
            onChange={(e) => setOptTexto(e.target.value)}
            onBlur={handleOptBlur}
            disabled={readOnly}
            data-testid="delta-optimista"
          />
          <span className="text-xs text-muted-foreground">pp</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Label
            htmlFor="delta-pesimista"
            className="text-xs whitespace-nowrap text-rose-700 dark:text-rose-400 font-medium"
          >
            Pesimista −
          </Label>
          <Input
            id="delta-pesimista"
            type="number"
            min="0"
            step="0.5"
            className="h-8 w-[72px] text-sm"
            value={pesTexto}
            onChange={(e) => setPesTexto(e.target.value)}
            onBlur={handlePesBlur}
            disabled={readOnly}
            data-testid="delta-pesimista"
          />
          <span className="text-xs text-muted-foreground">pp</span>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Se aplican al crecimiento de los ingresos; los costes vinculados reaccionan solos.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI comparativa: último año proyectado, 3 escenarios por métrica
// ---------------------------------------------------------------------------

function KpiEscenario({ metrica, escenarios, anio }) {
  const Icon = metrica.icon;
  const valor = (escId) => getAnio(escenarios?.[escId]?.[metrica.clave], anio);
  const vBase = valor("base");

  const deltaVsBase = (v) => {
    if (v === null || vBase === null || vBase === 0) return null;
    return ((v - vBase) / Math.abs(vBase)) * 100;
  };

  return (
    <Card data-testid={`kpi-escenario-${metrica.id}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          {LABEL_LARGO[metrica.id]}
          <span className="ml-auto text-[11px] font-normal uppercase tracking-wider">{anio}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {/* Base destacado */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-primary">Base</div>
          <div className="text-lg font-semibold font-mono tabular-nums">{formatCurrency(vBase)}</div>
        </div>
        {/* Optimista / Pesimista con delta vs base */}
        {ESCENARIOS.filter((e) => e.id !== "base").map((esc) => {
          const v = valor(esc.id);
          const d = deltaVsBase(v);
          return (
            <div key={esc.id} className="flex items-baseline justify-between gap-2">
              <span className={cn("text-[11px]", esc.texto)}>{esc.label}</span>
              <span className="flex items-baseline gap-1.5">
                <span className={cn("text-sm font-mono tabular-nums font-medium", esc.texto)}>
                  {formatCurrency(v)}
                </span>
                {d !== null && (
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {formatPctSigned(d)}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function EscenariosContent({ empresaId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noDisponible, setNoDisponible] = useState(false);
  const [error, setError] = useState(null);
  const [metricaId, setMetricaId] = useState("ventas");

  const cargar = useCallback(
    async (inicial = false) => {
      try {
        if (inicial) setLoading(true);
        setError(null);
        setNoDisponible(false);
        const [resEsc, resConfig] = await Promise.all([
          getProyeccionEscenarios(empresaId),
          getProyeccionConfig(empresaId).catch(() => ({ data: null })),
        ]);
        setData(resEsc.data);
        setConfig(resConfig.data);
      } catch (e) {
        // 404 o backend sin desplegar todavía → degradación elegante
        if (e?.response?.status === 404 || !e?.response) {
          setNoDisponible(true);
        } else {
          setError(e?.response?.data?.detail || "Error al cargar los escenarios");
        }
      } finally {
        if (inicial) setLoading(false);
      }
    },
    [empresaId]
  );

  useEffect(() => {
    cargar(true);
  }, [cargar]);

  // PUT parcial de config + recarga de escenarios
  const handleConfigGuardar = async (parcial, mensaje = "Escenarios actualizados") => {
    try {
      await saveProyeccionConfig(empresaId, parcial);
      toast.success(mensaje);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar los escenarios");
    }
  };

  const aniosHist = useMemo(
    () => [...(data?.anios_historicos || [])].map(Number).sort((a, b) => a - b),
    [data]
  );
  const aniosProy = useMemo(
    () => [...(data?.anios_proyectados || [])].map(Number).sort((a, b) => a - b),
    [data]
  );
  const ultimoHist = aniosHist[aniosHist.length - 1];
  const ultimoProy = aniosProy[aniosProy.length - 1];

  const escenarios = data?.escenarios;
  const metrica = METRICAS.find((m) => m.id === metricaId) || METRICAS[0];

  // Datos del gráfico: histórico (de la serie base) + 3 ramas proyectadas.
  // Las ramas arrancan en el último año histórico para que las líneas conecten.
  const chartData = useMemo(() => {
    if (!escenarios) return [];
    const serie = (escId) => escenarios?.[escId]?.[metrica.clave];
    return [...aniosHist, ...aniosProy].map((anio) => {
      const esHist = aniosHist.includes(anio);
      const vHist = esHist ? getAnio(serie("base"), anio) : null;
      const enlace = anio === ultimoHist ? vHist : null;
      return {
        anio: String(anio),
        historico: vHist,
        base: esHist ? enlace : getAnio(serie("base"), anio),
        optimista: esHist ? enlace : getAnio(serie("optimista"), anio),
        pesimista: esHist ? enlace : getAnio(serie("pesimista"), anio),
      };
    });
  }, [escenarios, metrica, aniosHist, aniosProy, ultimoHist]);

  const hayHistoricoEnGrafico = chartData.some((d) => d.historico !== null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (noDisponible) {
    return (
      <Card data-testid="escenarios-no-disponible">
        <CardContent className="py-12">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="p-3 rounded-full bg-muted">
              <ServerOff className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold">Escenarios no disponibles todavía</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Los escenarios estarán disponibles cuando se actualice el servidor.
                El resto de la proyección sigue funcionando con normalidad.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error || "Sin datos de escenarios"}</p>
        <p className="text-xs mt-1">Configura drivers en las pestañas de Ingresos y Gastos para ver escenarios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escenarios-proyeccion">
      {/* Header: título + deltas */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            Escenarios de la proyección
          </h3>
          <p className="text-sm text-muted-foreground">
            Compara el escenario base con una variante optimista y otra pesimista.
          </p>
        </div>
        <DeltasEscenarios config={config} readOnly={readOnly} onGuardar={handleConfigGuardar} />
      </div>

      {/* KPI comparativa: último año proyectado */}
      {ultimoProy != null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {METRICAS.map((m) => (
            <KpiEscenario key={m.id} metrica={m} escenarios={escenarios} anio={ultimoProy} />
          ))}
        </div>
      )}

      {/* Gráfico con selector de métrica */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle>{LABEL_LARGO[metrica.id]} por escenario</CardTitle>
            <div
              className="flex rounded-md border overflow-hidden self-start"
              role="radiogroup"
              aria-label="Métrica del gráfico"
            >
              {METRICAS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={metricaId === m.id}
                  onClick={() => setMetricaId(m.id)}
                  className={cn(
                    "px-2.5 py-1 text-xs transition-colors whitespace-nowrap",
                    metricaId === m.id
                      ? "bg-primary text-primary-foreground font-medium"
                      : "bg-transparent text-muted-foreground hover:bg-muted/40"
                  )}
                  data-testid={`metrica-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="anio" {...axisProps} />
                <YAxis
                  {...axisProps}
                  tickFormatter={fmtCompact}
                  width={56}
                />
                <Tooltip
                  cursor={lineCursor}
                  content={<ChartTooltip formatter={(value, name) => [formatCurrency(value), name]} />}
                />
                <Legend content={<ChartLegend />} />
                {hayHistoricoEnGrafico && (
                  <Line
                    type="monotone"
                    dataKey="historico"
                    name="Histórico"
                    stroke={GUNMETAL}
                    strokeWidth={2}
                    dot={{ r: 3, fill: GUNMETAL }}
                    connectNulls
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="base"
                  name="Base"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="optimista"
                  name="Optimista"
                  stroke={EMERALD}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: EMERALD }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="pesimista"
                  name="Pesimista"
                  stroke={ROSE}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: ROSE }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Tabla comparativa: 4 métricas × 3 escenarios, columnas = años proyectados */}
      {aniosProy.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Comparativa por años proyectados</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="min-w-[200px]">Métrica / Escenario</TableHead>
                  {aniosProy.map((anio) => (
                    <TableHead
                      key={anio}
                      className="text-right w-[110px] bg-primary/5 text-primary font-semibold"
                    >
                      {anio}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {METRICAS.map((m) => (
                  <Fragment key={m.id}>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableCell
                        colSpan={1 + aniosProy.length}
                        className="py-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                      >
                        {LABEL_LARGO[m.id]}
                      </TableCell>
                    </TableRow>
                    {ESCENARIOS.map((esc) => {
                      const porAnio = escenarios?.[esc.id]?.[m.clave];
                      return (
                        <TableRow
                          key={`${m.id}-${esc.id}`}
                          className={cn(
                            "hover:bg-muted/20",
                            esc.id === "base" && "font-medium"
                          )}
                        >
                          <TableCell className="pl-6 py-2">
                            <Badge variant="outline" className={cn("text-[11px]", esc.badge)}>
                              {esc.label}
                            </Badge>
                          </TableCell>
                          {aniosProy.map((anio) => (
                            <TableCell
                              key={anio}
                              className={cn(
                                "text-right font-mono text-sm tabular-nums py-2 bg-primary/5",
                                esc.id !== "base" && esc.texto
                              )}
                            >
                              {formatCurrency(getAnio(porAnio, anio))}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
